import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { BRAND, SERVICES } from "@/data/site";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/gallery", label: "Gallery" },
  { to: "/__l5e/assets-v1/6424b902-fd72-4ef7-b453-fe369b3b22d0/Stock-flooring-catalog.pdf", label: "Download Catalog", external: true },
  { to: "/reviews", label: "Reviews" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-background/85 backdrop-blur-md border-b border-border">
      <div className="container-page flex h-16 md:h-20 items-center justify-between gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV.map((item) =>
            item.label === "Services" ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <NavLink
                  to="/services"
                  className={({ isActive }) =>
                    cn(
                      "text-sm font-medium transition-colors link-underline",
                      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    )
                  }
                >
                  Services
                </NavLink>
                {servicesOpen && (
                  <div className="absolute top-full right-0 pt-3 w-72">
                    <div className="rounded-lg border border-border bg-surface shadow-elev p-2">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          className="block px-3 py-2 rounded-md text-sm hover:bg-secondary transition-colors"
                        >
                          {s.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : item.external ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium transition-colors link-underline text-muted-foreground hover:text-foreground"
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  cn(
                    "text-sm font-medium transition-colors link-underline",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  )
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
            className="hidden lg:inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-accent transition-colors"
          >
            <Phone className="h-4 w-4" /> {BRAND.phoneDisplay}
          </a>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
            <Link to="/contact">Free Estimate</Link>
          </Button>
        </div>

        <button
          aria-label="Open menu"
          aria-expanded={open}
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-secondary"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-surface">
          <div className="container-page py-4 flex flex-col gap-1">
            {NAV.map((n) =>
              n.external ? (
                <a
                  key={n.to}
                  href={n.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="py-3 px-2 rounded-md text-base font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  {n.label}
                </a>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "py-3 px-2 rounded-md text-base font-medium",
                      isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    )
                  }
                >
                  {n.label}
                </NavLink>
              )
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button asChild variant="outline">
                <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`}>
                  <Phone className="mr-1 h-4 w-4" /> Call
                </a>
              </Button>
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/contact" onClick={() => setOpen(false)}>Free Estimate</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
